import React from 'react';
import { IonButton, IonPopover, IonCheckbox, IonLabel } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { FilterIcon } from '../theme/Icons';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { ITask } from './TasksState';

const useStyles = createUseStyles({
    filterButton: {
        '--padding-start': '8px',
        '--padding-end': '8px',
        fontSize: '14px !important',
        fontWeight: '500 !important',
        height: 'auto',
        minHeight: '36px',
    },
    filterPopover: {
        '&::part(content)': {
            minWidth: 200,
            minHeight: 'auto',
            maxHeight: '80vh',
            overflowY: 'auto'
        }
    },
    filterSection: {
        padding: '0 0 12px 0'
    },
    sectionHeader: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--ion-text-color)',
        padding: '16px 16px 8px 16px',
        margin: '0'
    },
    filterCount: {
        fontSize: '12px',
        backgroundColor: 'var(--ion-color-primary)',
        color: 'white',
        borderRadius: '10px',
        padding: '2px 6px',
        marginLeft: '4px',
        minWidth: '18px',
        width: '18px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        right: '2px',
        top: '-6px',
        zIndex: 1000
    },
    filterButtonWrapper: {
        position: 'relative',
        display: 'inline-block'
    },
    filterOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px',
        cursor: 'pointer',
        minHeight: '40px',
        '&:hover': {
            backgroundColor: 'var(--ion-color-step-50, #f2f2f2)'
        }
    },
});

export interface TasksFilterState {
    tags: string[];
    sourceTypes: string[];
}

interface ITasksFilterControlProps {
    filters: TasksFilterState;
    onFiltersChange: (filters: TasksFilterState) => void;
    tasks: ITask[];
    triggerId?: string;
    iconOnly?: boolean;
}

export const TasksFilterControl: React.FC<ITasksFilterControlProps> = ({
    filters,
    onFiltersChange,
    tasks,
    triggerId = 'tasks-filter-trigger',
    iconOnly = false
}) => {
    const classes = useStyles();
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const mixpanel = useInstance(MixPanelEventEmitter);

    // Get tags that actually exist in the tasks
    const getAvailableTagOptions = () => {
        const availableTags = new Set<string>();
        tasks.forEach(task => {
            if (task.tags && Array.isArray(task.tags)) {
                task.tags.forEach((tag: string) => availableTags.add(tag));
            }
        });

        return Array.from(availableTags)
            .sort((a, b) => a.localeCompare(b))
            .map(tag => ({
                key: tag,
                label: tag
            }));
    };

    // Get source types that actually exist in the tasks
    const getAvailableSourceTypeOptions = () => {
        const availableSourceTypes = new Set<string>();
        tasks.forEach(task => {
            if (task.source && task.source.type) {
                availableSourceTypes.add(task.source.type);
            }
        });

        return Array.from(availableSourceTypes)
            .sort((a, b) => a.localeCompare(b))
            .map(sourceType => ({
                key: sourceType,
                label: sourceType === 'ai-generated' ? 'From Recording' : 'Added Manually'
            }));
    };

    const tagOptions = getAvailableTagOptions();
    const sourceTypeOptions = getAvailableSourceTypeOptions();

    const handleFilterChange = (category: keyof TasksFilterState, value: string, checked: boolean) => {
        const currentFilters = filters[category];
        const newFilters = checked
            ? [...currentFilters, value]
            : currentFilters.filter(f => f !== value);

        // Update filters immediately for responsive UI
        onFiltersChange({
            ...filters,
            [category]: newFilters
        });

        // Track filter usage
        try {
            if (checked) {
                mixpanel.track('Tasks Filter Applied', () => ({
                    'Filter Category': category,
                    'Filter Value': value,
                    'Total Active Filters': newFilters.length
                }));
            } else {
                mixpanel.track('Tasks Filter Cleared', () => ({
                    'Cleared Filter Category': category,
                    'Remaining Active Filters': newFilters.length
                }));
            }
        } catch (error) {
            // Silently continue if tracking fails
        }
    };

    const getTotalActiveFilters = () => {
        return Object.values(filters).reduce((total, filterArray) => total + filterArray.length, 0);
    };

    const activeFilterCount = getTotalActiveFilters();

    const renderFilterSection = (
        title: string,
        category: keyof TasksFilterState,
        options: { key: string; label: string }[]
    ) => (
        <div className={classes.filterSection}>
            <div className={classes.sectionHeader}>{title}</div>
            <div>
                {options.map(option => (
                    <div
                        key={option.key}
                        className={classes.filterOption}
                        onClick={() => handleFilterChange(category, option.key, !filters[category].includes(option.key))}
                        data-id={`filter-${category}-${option.key}`}
                    >
                        <IonCheckbox
                            checked={filters[category].includes(option.key)}
                            onIonChange={(e) => {
                                e.stopPropagation();
                                handleFilterChange(category, option.key, e.detail.checked);
                            }}
                        />
                        <IonLabel style={{ marginLeft: '6px', fontSize: '14px' }}>{option.label}</IonLabel>
                    </div>
                ))}
            </div>
        </div>
    );


    return (
        <>
            <div className={classes.filterButtonWrapper}>
                <IonButton
                    fill="clear"
                    color="medium"
                    id={triggerId}
                    onClick={() => setPopoverOpen(true)}
                    className={classes.filterButton}
                    data-id="tasks-filter-button"
                >
                    <FilterIcon size="medium" color="medium" />
                    {!iconOnly && <span style={{ marginLeft: '4px' }}>Filter</span>}
                </IonButton>
                {activeFilterCount > 0 && (
                    <span className={classes.filterCount} data-id="tasks-filter-count">{activeFilterCount}</span>
                )}
            </div>

            <IonPopover
                isOpen={popoverOpen}
                trigger={triggerId}
                alignment="end"
                onDidDismiss={() => setPopoverOpen(false)}
                className={classes.filterPopover}
            >
                <div style={{ padding: '8px 0' }}>
                    {sourceTypeOptions.length > 0 && (
                        renderFilterSection('Source', 'sourceTypes', sourceTypeOptions)
                    )}
                    {tagOptions.length > 0 ? (
                        renderFilterSection('Tags', 'tags', tagOptions)
                    ) : sourceTypeOptions.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--ion-color-medium)' }}>
                            No filters available
                        </div>
                    ) : null}
                </div>
            </IonPopover>
        </>
    );
};
